import { AEMPageType } from "@AEM/models/AEMPage.model"
import { Role } from "@utils/constants.util"

enum ToolCode {
	SEE_OUR_SHIPS = "SEE-OUR-SHIPS",
	INSIGHT = "INSIGHT",
	SEARCH_PROMOTIONS = "SEARCH-PROMOTIONS",
	COUPON = "COUPON",
	TRAVEL_DOCS = "GUEST-DOCS"
}

// Map of AEM page types to required tool codes
export const PAGE_TOOL_CODE_MAP: Record<string, string[]> = {
	[AEMPageType.SEE_OUR_SHIPS]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_OVERVIEW]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_ENTERTAINMENT]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_ACTIVITIES]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_BAR_AND_LOUNGE]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_ROOMS]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_FLEET]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_DINING]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_SPECIALTY_DINING]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_COMPLIMENTARY_DINING]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_DECK_PLANS]: [ToolCode.SEE_OUR_SHIPS],
	[AEMPageType.SEE_OUR_SHIPS_NEIGHBOURHOODS]: [ToolCode.SEE_OUR_SHIPS],

	[AEMPageType.PAGES_INSIGHTS_OVERVIEW]: [ToolCode.INSIGHT],
	[AEMPageType.PAGES_INSIGHTS_ALERT_CENTER]: [ToolCode.INSIGHT],
	[AEMPageType.PAGES_INSIGHTS_COUPON_DETAILS]: [
		ToolCode.INSIGHT,
		ToolCode.COUPON
	],
	[AEMPageType.PAGES_INSIGHTS_FUTURE_COMPENSATION]: [ToolCode.INSIGHT],
	[AEMPageType.PAGES_INSIGHTS_INDIVIDUAL_RESERVATIONS]: [ToolCode.INSIGHT],
	[AEMPageType.PAGES_INSIGHTS_NPC_TRANSMISSION]: [ToolCode.INSIGHT],
	[AEMPageType.PAGES_INSIGHTS_AUTOMATED_EMAILS]: [ToolCode.INSIGHT],
	[AEMPageType.PAGES_INSIGHTS_VIEW_BRANCH]: [ToolCode.INSIGHT],

	[AEMPageType.PROMOTIONS_HOME]: [ToolCode.SEARCH_PROMOTIONS],
	[AEMPageType.FEATURED_OFFERS]: [ToolCode.SEARCH_PROMOTIONS],
	[AEMPageType.ARCHIVED_OFFERS]: [ToolCode.SEARCH_PROMOTIONS],
	[AEMPageType.OFFERS]: [ToolCode.SEARCH_PROMOTIONS],
	[AEMPageType.EXPIRED_OFFERS]: [ToolCode.SEARCH_PROMOTIONS],
	[AEMPageType.PAGES_PROMOTIONS_BRAND_LANDING_ROYAL]: [
		ToolCode.SEARCH_PROMOTIONS
	],
	[AEMPageType.PAGES_PROMOTIONS_OCTOBER_OFFER]: [ToolCode.SEARCH_PROMOTIONS],
	[AEMPageType.PAGES_PROMOTIONS_SEARCH_RESULTS]: [ToolCode.SEARCH_PROMOTIONS]
}

// Map of routes to required tool codes
export const ROUTE_TOOL_CODE_MAP: Record<string, ToolCode[]> = {
	"/explore-our-fleet": [ToolCode.SEE_OUR_SHIPS],
	"/promotion/home": [ToolCode.SEARCH_PROMOTIONS],
	"/promotion/brand": [ToolCode.SEARCH_PROMOTIONS],
	"/promotion/results": [ToolCode.SEARCH_PROMOTIONS],
	"/promotion/detail": [ToolCode.SEARCH_PROMOTIONS],
	"/promotion/featured-offers": [ToolCode.SEARCH_PROMOTIONS],
	"/promotion/archived-offers": [ToolCode.SEARCH_PROMOTIONS],
	"/promotion/offers-list": [ToolCode.SEARCH_PROMOTIONS],
	"/travel-docs": [ToolCode.TRAVEL_DOCS],
	"/travel-docs/*": [ToolCode.TRAVEL_DOCS],
	"/insights": [ToolCode.INSIGHT],
	"/insights/overview": [ToolCode.INSIGHT],
	"/insights/alert-center": [ToolCode.INSIGHT],
	"/insights/coupon-application-details": [ToolCode.INSIGHT, ToolCode.COUPON],
	"/insights/future-compensation": [ToolCode.INSIGHT],
	"/insights/individual-reservations": [ToolCode.INSIGHT],
	"/insights/npc-transmission-details": [ToolCode.INSIGHT],
	"/insights/set-up-automated-emails": [ToolCode.INSIGHT],
	"/insights/view-another-branch": [ToolCode.INSIGHT]
}

/**
 * Checks if a user has permission to access a specific route
 * @param route The route path to check
 * @returns array of tool codes required to access the route
 */
export function getRouteToolCodes(route: string): ToolCode[] | undefined {
	const lowerRoute = route.toLowerCase()
	const matchingRoute = Object.keys(ROUTE_TOOL_CODE_MAP)
		.filter((path) => {
			if (path.toLowerCase() === lowerRoute) return true
			if (path.endsWith("*")) {
				const basePath = path.slice(0, -1).toLowerCase()
				return lowerRoute.startsWith(basePath)
			}
			return false
		})
		.sort((a, b) => b.length - a.length)[0]

	return ROUTE_TOOL_CODE_MAP[matchingRoute]
}

/**
 * Formats AEM codes by extracting the value after the slash
 * @param aemCodes Array of strings in format "text-moretext/value"
 * @returns array of values (the part after the slash)
 */
export function formatAEMCodes(aemCodes?: string[]): string[] {
	const codes: string[] = []
	for (const code of aemCodes ?? []) {
		const match = code.match(/\/(.+)$/)
		if (match?.[1]) {
			codes.push(match[1].toUpperCase().trim())
		}
	}
	return codes
}

/** Single toolCode / appId from AEM: path segments via {@link formatAEMCodes}, else raw string or first array entry. */
export function formatAEMCodeScalar(
	value?: string | string[]
): string | undefined {
	const list = !value ? [] : Array.isArray(value) ? value : [value]
	return (
		formatAEMCodes(list)[0] ?? (typeof value === "string" ? value : value?.[0])
	)
}

/**
 * Checks if a user has admin role (ADMIN or RCCLADMIN)
 * @param roleCode The user's role code
 * @returns boolean indicating if user is an admin
 */
export const isAdmin = (roleCode?: string): boolean => {
	return roleCode === Role.ADMIN
}

/**
 * Checks if a user has admin role (ADMIN or RCCLADMIN)
 * @param roleCode The user's role code
 * @returns boolean indicating if user is an admin
 */
export const isAdminOrRCCLAdmin = (roleCode?: string): boolean => {
	return roleCode === Role.ADMIN || roleCode === Role.RCCLADMIN
}

/**
 * Checks if a user has superuser role (RCCL or RCCLADMIN)
 * @param roleCode The user's role code
 * @returns boolean indicating if user is a superuser
 */
export const isSuperUser = (roleCode?: string): boolean => {
	return roleCode === Role.RCCL || roleCode === Role.RCCLADMIN
}
